import { useRef, type ReactNode, type ReactElement } from "react";
import { ShellRightRailOccupiedContext } from "../lib/shell-right-rail-context.js";

/** The main landmark's stable id — the skip link's target. */
export const SHELL_MAIN_ID = "shell-main";

export interface ShellProps {
  /** Top bar element (already wired by the host app). */
  topBar: ReactNode;
  /** Optional right-side rail — used for the Player AI panel. Omit until needed. */
  rightRail?: ReactNode;
  /** Whether the slot reserves page width. Overlays may share the same live
   * slot; retained docked exits keep this true until their width is released. */
  rightRailOccupied?: boolean;
  /** Main content area — page route content goes here. */
  children: ReactNode;
  /** Text direction; default "ltr". Set "rtl" when an RTL locale is active. */
  dir?: "ltr" | "rtl";
  /** BCP-47 language tag; default "en". */
  lang?: string;
  /**
   * Label for a "skip to main content" link rendered as the FIRST focusable
   * element (WCAG 2.4.1; PB-5175 S1). Visually hidden until it holds focus.
   * Activation moves focus straight into `main` without a hash navigation, so
   * a host whose tab sync reads the location never sees a change. Omitted,
   * nothing renders.
   */
  skipLinkLabel?: string;
}

/**
 * Three-region layout: top bar (full width), main content (scrollable), optional right rail.
 * Data-blind — every region is filled by the host app via props.
 *
 * Carries the i18n direction/language pathway: `dir`/`lang` default to ltr/en
 * and propagate from the shell root, so enabling RTL or another language later
 * is a prop change, not a layout rewrite (paired with the logical-properties
 * lint rule in this package).
 */
export function Shell({
  topBar,
  rightRail,
  rightRailOccupied = rightRail != null && rightRail !== false,
  children,
  dir = "ltr",
  lang = "en",
  skipLinkLabel,
}: ShellProps): ReactElement {
  const mainRef = useRef<HTMLElement>(null);

  return (
    <div
      dir={dir}
      lang={lang}
      className="flex flex-col h-screen bg-pb-shell text-pb-text overflow-hidden"
    >
      {skipLinkLabel ? (
        // eslint-disable-next-line ds/no-hand-built -- the skip link: a same-page anchor that moves focus to main; Link is for navigating to a destination
        <a
          href={`#${SHELL_MAIN_ID}`}
          onClick={(event) => {
            // Focus the landmark directly rather than navigating to its hash:
            // the host's URL-driven tab sync must never see a location change.
            event.preventDefault();
            mainRef.current?.focus();
          }}
          className="sr-only focus:not-sr-only focus:absolute focus:start-2 focus:top-2 focus:z-tooltip focus:rounded-md focus:bg-pb-card-elevated focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-pb-text focus:outline-none focus:ring-2 focus:ring-pb-teal"
        >
          {skipLinkLabel}
        </a>
      ) : null}
      {topBar}
      <div className="flex flex-1 min-h-0">
        <main
          id={SHELL_MAIN_ID}
          ref={mainRef}
          tabIndex={-1}
          data-shell-main
          className="flex-1 min-h-0 overflow-y-auto bg-pb-page min-w-0 outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-pb-teal"
        >
          {/* The rail takes width from main without changing the window, so
              frames read this instead of a media query (PB-5436). */}
          <ShellRightRailOccupiedContext.Provider value={rightRailOccupied}>
            {children}
          </ShellRightRailOccupiedContext.Provider>
        </main>
        {rightRail}
      </div>
    </div>
  );
}
